import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { GroupContext, GroupContextValue } from './GroupContext';
import { SupabaseGroupsService, GroupData } from '@/services/supabase-groups';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { useAuth } from '@/hooks/use-auth';

interface SupabaseGroupProviderProps {
  children: React.ReactNode;
}

export function SupabaseGroupProvider({ children }: SupabaseGroupProviderProps) {
  const supabase = useSupabase();
  const { isAuthenticated } = useAuth();
  const groupsService = useMemo(() => new SupabaseGroupsService(supabase), [supabase]);
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [allGroups, setAllGroups] = useState<GroupData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Utility to flatten nested groups
  const flattenGroups = (groups: GroupData[]): GroupData[] => {
    const result: GroupData[] = [];
    const traverse = (groupList: GroupData[]) => {
      groupList.forEach(g => {
        result.push(g);
        if (g.subgroups && g.subgroups.length > 0) {
          traverse(g.subgroups);
        }
      });
    };
    traverse(groups);
    return result;
  };

  const loadGroups = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('[SupabaseGroupProvider] Not authenticated, skipping groups load');
      setGroups([]);
      setAllGroups([]);
      setIsLoading(false);
      return;
    }
    console.log('[SupabaseGroupProvider] Starting groups load');
    setIsLoading(true);
    try {
      const fetchedGroups = await groupsService.getGroups();
      console.log('[SupabaseGroupProvider] Groups loaded:', {
        hierarchicalCount: fetchedGroups.length,
        groups: fetchedGroups.map(g => ({ id: g.id, name: g.name, subgroupCount: g.subgroups?.length || 0 }))
      });
      const flattened = flattenGroups(fetchedGroups);
      console.log('[SupabaseGroupProvider] Groups flattened:', {
        flatCount: flattened.length,
        groups: flattened.map(g => ({ id: g.id, name: g.name }))
      });
      setGroups(fetchedGroups);
      setAllGroups(flattened);
    } catch (error) {
      console.error('[SupabaseGroupProvider] Failed to load groups:', error);
    } finally {
      setIsLoading(false);
    }
  }, [groupsService, isAuthenticated]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups, isAuthenticated]);

  const createGroup = async (name: string, description: string, parentId?: string) => {
    const success = await groupsService.createGroup(name, description, parentId);
    if (success) {
      await loadGroups(); // Refresh the groups list
    }
  };

  const addMemberToGroup = async (groupId: string, userId: string) => {
    const success = await groupsService.addMemberToGroup(groupId, userId);
    if (success) {
      await loadGroups(); // Refresh the groups list
    }
  };

  const removeMemberFromGroup = async (groupId: string, userId: string) => {
    const success = await groupsService.removeMemberFromGroup(groupId, userId);
    if (success) {
      await loadGroups(); // Refresh the groups list
    }
  };

  const contextValue: GroupContextValue = {
    groups,
    allGroups,
    isLoading,
    createGroup,
    addMemberToGroup,
    removeMemberFromGroup,
    refreshGroups: loadGroups
  };

  return (
    <GroupContext.Provider value={contextValue}>
      {children}
    </GroupContext.Provider>
  );
}
